import { useCallback } from "react";
import { Link, useNavigate } from "react-router";

import { getImageUrl } from "@/lib/aws-amplify/host-storage";
import { cn } from "@/lib/chillnn/cn";
import { getTodaysDate } from "@/lib/dayjs/get-todays-date";
import {
	BillingTypeTargetForOption,
	BillingTypeTerm,
	OptionType,
	SaleTypeForPlan,
	SaleTypeForTerm,
	type Option,
} from "@/lib/gql/__generated__/graphql";

import { IconButtonList } from "@/components/features/items/edit/_shared/ui/components/system/list/list-item/list-icon-buttons";

import DragIndicator from "@/components/ui/icon/drag-indicator";
import InfinityIcon from "@/components/ui/icon/infinity";
import { Image } from "@/components/ui/image/image";
import { Label } from "@/components/ui/label/label";
import { TableCell } from "@/components/ui/table/table";

type Props = {
	option: Option;
	editOptionPagePath: { edit: (optionID: string) => string; createNewItem: string };

	onDeleteOption: () => Promise<void>;
	onTransitionToEdit: () => void;
	isSorting?: boolean;
};

export const OptionListPageTableItem = ({
	option,
	editOptionPagePath,
	onDeleteOption,
	onTransitionToEdit,
	isSorting,
}: Props) => {
	const imageURL = getImageUrl(option.mainKeyVisual);

	const navigate = useNavigate();
	const todaysDate = getTodaysDate();

	const isOutOfPeriod =
		option.saleTypeForTerm === SaleTypeForTerm.Limited &&
		!!option.saleStart &&
		!!option.saleEnd &&
		!(option.saleStart <= todaysDate && todaysDate <= option.saleEnd);

	const isOutOfStock = (option.maxStockNum ?? -1) >= 0 ? option.maxStockNum === option.soldNumTotal : false;

	const billingTypeTermText = option.billingTypeTerm === BillingTypeTerm.PerDate ? "１泊ごと" : "宿泊を通して";
	const billingTypeTargetText =
		option.billingTypeTarget === BillingTypeTargetForOption.Group
			? "１団体あたり"
			: option.billingTypeTarget === BillingTypeTargetForOption.Person
				? "１人あたり"
				: option.billingTypeTarget === BillingTypeTargetForOption.Room
					? "１部屋あたり"
					: option.billingTypeTarget === BillingTypeTargetForOption.PersonOnlyKids
						? "子ども１人あたり"
						: "";
	const saleUnitText =
		option.optionType === OptionType.Goods ? "１個あたり" : billingTypeTermText + billingTypeTargetText;

	const saleTypeForPlanText = option?.saleTypeForPlan === SaleTypeForPlan.Limited ? "プラン限定" : "通常";

	const isSubOption = option.useAsSubOption;

	const onCopyOption = useCallback(() => {
		navigate(editOptionPagePath.createNewItem + `?duplicateItemID=${option.optionID}`);
	}, [navigate, editOptionPagePath, option.optionID]);

	return (
		<>
			<TableCell className={cn(!isSorting && "hidden")}>
				<div className="grid place-content-center">
					<div className="shrink-0 rounded p-1 hover:cursor-grab hover:bg-zinc-100">
						<DragIndicator size="default" />
					</div>
				</div>
			</TableCell>
			{/* オプション名 */}
			<TableCell className="flex items-center gap-x-3">
				<Image src={imageURL} className="h-16 w-16 min-w-16 shrink-0 rounded object-cover" />

				<div className={cn("flex flex-col gap-y-1.5", isSubOption && "self-start")}>
					{isSubOption ? <Label text="サブオプション" variant="purple" className="w-fit" size="xs" /> : null}
					<Link
						to={editOptionPagePath.edit(option.optionID)}
						className="underline-offset-4 hover:underline"
						viewTransition
					>
						{option.name}
					</Link>
				</div>
			</TableCell>
			{/* ステータス */}
			<TableCell className="text-center">
				<Label variant={option.inSale ? "green" : "pink"} text={option.inSale ? "公開" : "非公開"} />
			</TableCell>
			{/* 販売タイプ */}
			<TableCell className="text-center">
				<p>{saleTypeForPlanText}</p>
			</TableCell>
			{/* 残在庫 */}
			<TableCell className="text-center">
				{!isOutOfPeriod && !isOutOfStock ? (
					option.maxStockNum ? (
						<p>{option.maxStockNum - (option.soldNumTotal || 0)}</p>
					) : (
						<div className="flex justify-center">
							<InfinityIcon size="sm" />
						</div>
					)
				) : (
					<p>{isOutOfPeriod ? "販売期間外" : isOutOfStock ? "在庫切れ" : null}</p>
				)}
			</TableCell>
			{/* 単価 */}
			<TableCell className="text-right">
				{!isSubOption ? (
					<div>
						<p>{saleUnitText}</p>
						<p>
							<span className="font-semibold">{(option?.price?.amount ?? 0).toLocaleString()}円</span>
						</p>
					</div>
				) : null}
			</TableCell>
			{/* 操作 */}
			<TableCell>
				<div className="flex justify-center">
					<IconButtonList
						onClickCopyButton={onCopyOption}
						onClickDeleteButton={onDeleteOption}
						onClickEditButton={onTransitionToEdit}
					/>
				</div>
			</TableCell>
		</>
	);
};
